/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
// reactstrap components
import { Card, CardHeader, Table, Container, Row, Button } from 'reactstrap';
import { GlobalContext } from '../api/GlobalContext';
import { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import HashState from '../variables/hash';

// core components
import Header from 'components/Headers/Header';
import LoadingBanner from 'components/Misc/LoadingBanner';

const BASE_URL = 'https://www.comprehendhealth.ai/referrals';
const REFERRAL_QUERY_PARAM = 'referralCode';
const FULL_NAME_QUERY_PARAM = 'fullName';
// https://www.comprehendhealth.ai/referrals?referralCode=ALACOM&fullName=Alana%20Isaacs
const EMAIL_SOLICITATION_MESSAGE =
  "Hey, I'm letting AI do my notes these days with a new tool called Comprehend PT. Now I spend <10 minutes/day on documentation! Use my link to check it out so you can too! (Now compatible with 25+ EMR's)";
const SMS_SOLICITATION_MESSAGE =
  "Hey, I'm letting AI do my notes. Now I spend <10 minutes/day on documentation!";
const TWITTER_MESSAGE =
  'Using AI for my notes now takes <10 mins/day! Check out Comprehend PT';

const TwitterShareButton = ({ referralCode }) => {
  const tweetText =
    TWITTER_MESSAGE +
    ' ' +
    `Use my referral code "${referralCode}" to get a 14 day on free trial!`;
  const via = 'ComprehendPT'; // Handle
  const hashtags =
    'ComprehendPT,ComprehendHealth,AINotes,PTNotes,AIDocumentation,AIMedicalScribe';
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&via=${via}&hashtags=${hashtags}`;

  return (
    <Button
      color="primary"
      onClick={() => {
        window.open(twitterUrl, '_blank');
      }}
    >
      Tweet
    </Button>
  );
};

// TODO - Not working
const FacebookShareButton = ({ referralLink, referralCode }) => {
  const facebookText =
    TWITTER_MESSAGE +
    ' ' +
    `Use my referral code "${referralCode}" to get a 14 day on free trial!`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${referralLink}&quote=${facebookText}!`;

  return (
    <Button
      color="primary"
      onClick={() => {
        window.open(facebookUrl, '_blank');
      }}
    >
      Share on Facebook
    </Button>
  );
};

const Referrals = () => {
  // Allow for users to refer others to the platform!
  // Make it easy for them to send an email, text, share to socials with their code and a link
  const navigate = useNavigate();

  // Get our Globals
  const { apiClient } = useContext(GlobalContext);

  const [referralCode, setReferralCode] = useState(apiClient.referralCode);
  const [referralLink, setReferralLink] = useState('');
  const [mailtoLink, setMailtoLink] = useState('');
  const [smsLink, setSmsLink] = useState('');
  const [gmailLink, setGmailLink] = useState('');

  // If the referral code every changes we need to update these
  useEffect(() => {
    if (referralCode) {
      let localLink = `${BASE_URL}?${REFERRAL_QUERY_PARAM}=${referralCode}&${FULL_NAME_QUERY_PARAM}=${apiClient.name}`;
      let mailToAndGmailLink = localLink
        .replace('&', '%26')
        .replace(' ', '%20');

      const emailSubject = 'AI Medical Scribe - Comprehend PT Referral';
      const emailBody = `${EMAIL_SOLICITATION_MESSAGE}\nSign up here: ${mailToAndGmailLink}`;
      setSmsLink(
        `sms:?body=${SMS_SOLICITATION_MESSAGE}. Use my referral code: '${referralCode}' or Sign up here: ${mailToAndGmailLink}`,
      );

      setGmailLink(
        `https://mail.google.com/mail/?view=cm&fs=1&to=&su=${encodeURIComponent(
          emailSubject,
        )}&body=${encodeURIComponent(emailBody)}`,
      );

      setReferralLink(localLink);
    }
  }, [referralCode]);

  // If the referral Code is not set yet we are getting it via the stat, try again in 500ms up to 10 times
  const [retryCount, setRetryCount] = useState(0);
  useEffect(() => {
    if (!referralCode && retryCount < 10) {
      setTimeout(() => {
        setReferralCode(apiClient.referralCode);
        setRetryCount(retryCount + 1);
      }, 500);
    }
  }, [referralCode, retryCount, apiClient.referralCode]);

  const [copyClicked, setCopyClicked] = useState(false);
  function copyClickedFunc() {
    navigator.clipboard.writeText(referralLink);
    setCopyClicked(true);
    setTimeout(() => {
      setCopyClicked(false);
    }, 2000);
  }

  return (
    <>
      <Header isLite={true} />
      {/* Page content */}
      <LoadingBanner
        showBanner={copyClicked}
        loadingText={'Link Copied to Clipboard'}
      />
      <Container className="mt--7" fluid>
        {/* Table */}
        <Row>
          <div className="col">
            <Card className="shadow">
              <div className="mt-3 mb-3 mr-3 ml-3">
                <h1>Refer your Friends!</h1>
                <p>
                  Invite your friends to try Comprehend with your referral link
                  or promo code! They'll get a special discount, and{' '}
                  <b>you'll save $10 on your monthly bill</b>. Start sharing
                  now!
                </p>
                <h3>
                  Your Referral Code: <b>{referralCode}</b>
                </h3>
                <div className="mt-3 mb-3">
                  <Button
                    color="primary"
                    onClick={() => {
                      window.open(referralLink, '_blank');
                    }}
                  >
                    Open Page
                  </Button>
                  <Button color="primary" onClick={copyClickedFunc}>
                    Copy Link
                  </Button>
                </div>
                <div className="mt-3">
                  <Button
                    color="primary"
                    onClick={() => {
                      window.open(gmailLink, '_blank');
                    }}
                  >
                    Email
                  </Button>
                  {/* <Button
                    color="primary"
                    onClick={() => {
                      window.location.href = smsLink;
                    }}
                  >
                    SMS
                  </Button> */}
                </div>
                {/* <TwitterShareButton referralLink={referralCode}/> */}
              </div>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default Referrals;
